import { motion, useMotionValue, useReducedMotion, useSpring } from "motion/react";
import { useRef, useState, forwardRef } from "react";
import type { ReactNode, MouseEvent } from "react";
import { cn } from "@/lib/utils";

type Variant = "dark" | "light" | "outline-dark" | "outline-light";

const variantStyles: Record<Variant, { base: string; fill: string; hoverText: string }> = {
  dark: {
    base: "bg-foreground text-background",
    fill: "bg-background",
    hoverText: "group-hover:text-white",
  },
  light: {
    base: "bg-background text-foreground",
    fill: "bg-foreground",
    hoverText: "group-hover:text-white",
  },
  "outline-dark": {
    base: "border border-foreground/20 text-foreground",
    fill: "bg-foreground",
    hoverText: "group-hover:text-white",
  },
  "outline-light": {
    base: "border border-background/30 text-background",
    fill: "bg-background",
    hoverText: "group-hover:text-white",
  },
};

type CommonProps = {
  children: ReactNode;
  className?: string;
  variant?: Variant;
  immersive?: boolean;
  avatar?: string;
};

type AnchorProps = CommonProps & React.AnchorHTMLAttributes<HTMLAnchorElement> & { as?: string };
type ButtonProps = CommonProps & React.ButtonHTMLAttributes<HTMLButtonElement> & { as: string };

export const MagneticButton = forwardRef<HTMLElement, AnchorProps | ButtonProps>(
  function MagneticButton(props, _ref) {
    const { children, className, variant = "dark", avatar, immersive: _immersive, ...rest } = props as AnchorProps & { as?: string };
    const wrapRef = useRef<HTMLDivElement>(null);
    const styles = variantStyles[variant];
    const reduceMotion = useReducedMotion();

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const x = useSpring(mx, { stiffness: 200, damping: 18, mass: 0.5 });
    const y = useSpring(my, { stiffness: 200, damping: 18, mass: 0.5 });

    const [origin, setOrigin] = useState({ x: 50, y: 50 });
    const [hover, setHover] = useState(false);

    const handleMove = (e: MouseEvent) => {
      const el = wrapRef.current;
      if (!el || reduceMotion) return;
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      mx.set((e.clientX - cx) * 0.25);
      my.set((e.clientY - cy) * 0.25);
    };

    const handleEnter = (e: MouseEvent) => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setOrigin({
        x: ((e.clientX - r.left) / r.width) * 100,
        y: ((e.clientY - r.top) / r.height) * 100,
      });
      setHover(true);
    };

    const handleLeave = (e: MouseEvent) => {
      const el = wrapRef.current;
      if (el) {
        const r = el.getBoundingClientRect();
        setOrigin({
          x: ((e.clientX - r.left) / r.width) * 100,
          y: ((e.clientY - r.top) / r.height) * 100,
        });
      }
      mx.set(0);
      my.set(0);
      setHover(false);
    };

    const inner = (
      <>
        <span
          aria-hidden
          className="pointer-events-none absolute rounded-full transition-transform duration-200 ease-out"
          style={{
            left: `${origin.x}%`,
            top: `${origin.y}%`,
            width: "250%",
            height: "250%",
            transform: `translate(-50%, -50%) scale(${hover ? 1 : 0})`,
            transformOrigin: "center",
            backgroundColor: "#002BFF",
          }}
        />
        <span className={cn("relative z-10 inline-flex items-center gap-2 transition-colors duration-150", styles.hoverText)}>
          {avatar && (
            <img
              src={avatar}
              alt=""
              width={20}
              height={20}
              loading="eager"
              decoding="async"
              className="h-5 w-5 rounded-full object-cover"
              style={{ flexShrink: 0 }}
            />
          )}
          {children}
        </span>
      </>
    );

    const classes = cn(
      "group relative inline-flex items-center gap-2 overflow-hidden px-5 py-3 text-sm font-medium min-h-11 isolate",
      styles.base,
      className,
    );
    const buttonStyle: React.CSSProperties = { borderRadius: "10px" };
    const as = (props as AnchorProps).as ?? "a";

    return (
      <motion.div
        ref={wrapRef}
        style={
          reduceMotion
            ? { display: "inline-block", position: "relative", zIndex: 70 }
            : { x, y, display: "inline-block", position: "relative", zIndex: 70 }
        }
        onMouseMove={handleMove}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        {as === "button" ? (
          <button className={classes} style={buttonStyle} {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}>
            {inner}
          </button>
        ) : (
          <a className={classes} style={buttonStyle} {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}>
            {inner}
          </a>
        )}
      </motion.div>
    );
  },
);
